import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { publicClient } from "@/lib/public-supabase.server";

export type TrackAggregate = {
  position: string;
  title: string;
  score: number;
  votes: number;
};

export type AlbumRating = {
  score: number | null;
  raters: number;
  ratedTracks: number;
  tracks: TrackAggregate[];
};

export type MyTrackRating = { position: string; rating: number };

export type RatingSummary = { discogsId: string; score: number | null; raters: number };

export type CollectorAvatar = {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
};

export type CollectorsSummary = { discogsId: string; count: number; people: CollectorAvatar[] };

function median(values: number[]): number {
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

function aggregate(rows: { user_id: string; track_position: string; track_title: string; rating: number }[]): AlbumRating {
  if (rows.length === 0) return { score: null, raters: 0, ratedTracks: 0, tracks: [] };
  const byTrack = new Map<string, { title: string; values: number[] }>();
  for (const r of rows) {
    const entry = byTrack.get(r.track_position) ?? { title: r.track_title, values: [] };
    entry.values.push(Number(r.rating));
    byTrack.set(r.track_position, entry);
  }
  const tracks: TrackAggregate[] = [...byTrack.entries()].map(([position, v]) => ({
    position,
    title: v.title,
    score: Math.round(median(v.values) * 10) / 10,
    votes: v.values.length,
  }));
  return {
    score: Math.round(median(tracks.map((t) => t.score)) * 10) / 10,
    raters: new Set(rows.map((r) => r.user_id)).size,
    ratedTracks: tracks.length,
    tracks,
  };
}

/** Notas públicas do álbum: mediana por faixa e mediana geral. */
export const getAlbumRating = createServerFn({ method: "GET" })
  .inputValidator((i: { discogsId: string }) => i)
  .handler(async ({ data }): Promise<AlbumRating> => {
    const sb = publicClient();
    const { data: rows, error } = await sb
      .from("album_track_ratings")
      .select("user_id, track_position, track_title, rating")
      .eq("discogs_id", data.discogsId);
    if (error) throw new Error(error.message);
    return aggregate((rows ?? []) as any);
  });

/** Minhas notas para as faixas de um disco. */
export const getMyAlbumRating = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { discogsId: string }) => i)
  .handler(async ({ data, context }): Promise<MyTrackRating[]> => {
    const { data: rows, error } = await context.supabase
      .from("album_track_ratings")
      .select("track_position, rating")
      .eq("discogs_id", data.discogsId)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return (rows ?? []).map((r: any) => ({ position: r.track_position, rating: Number(r.rating) }));
  });

/** Salva (ou remove, com rating 0) a nota de uma faixa e registra a atividade. */
export const rateTrack = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: {
    discogsId: string;
    position: string;
    title: string;
    rating: number;
    albumTitle?: string;
    albumArtist?: string;
  }) => {
    if (!i.discogsId || !i.position) throw new Error("Faixa inválida");
    const rating = Math.round(Number(i.rating) * 2) / 2;
    if (rating !== 0 && (rating < 1 || rating > 5)) throw new Error("Nota inválida");
    return { ...i, rating, title: (i.title || i.position).slice(0, 200) };
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    if (data.rating === 0) {
      const { error } = await supabase
        .from("album_track_ratings")
        .delete()
        .eq("user_id", userId)
        .eq("discogs_id", data.discogsId)
        .eq("track_position", data.position);
      if (error) throw new Error(error.message);
      return { ok: true, removed: true };
    }

    const { error } = await supabase.from("album_track_ratings").upsert(
      {
        user_id: userId,
        discogs_id: data.discogsId,
        track_position: data.position,
        track_title: data.title,
        rating: data.rating,
      },
      { onConflict: "user_id,discogs_id,track_position" },
    );
    if (error) throw new Error(error.message);

    // Atividade agrupada: um post por pessoa/disco, atualizado a cada nova nota.
    const { count } = await supabase
      .from("album_track_ratings")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("discogs_id", data.discogsId);

    const { data: mine } = await supabase
      .from("user_albums")
      .select("id, title, artist")
      .eq("user_id", userId)
      .eq("discogs_id", data.discogsId)
      .limit(1)
      .maybeSingle();

    const total = count ?? 1;
    const label = data.albumTitle ?? mine?.title ?? "um disco";
    const body =
      total === 1
        ? `avaliou uma faixa de ${label}`
        : `avaliou ${total} faixas de ${label}`;

    const { data: existingPost } = await supabase
      .from("feed_posts")
      .select("id")
      .eq("user_id", userId)
      .eq("type", "rated_album")
      .eq("album_id", mine?.id ?? "")
      .limit(1)
      .maybeSingle();

    if (mine?.id) {
      if (existingPost?.id) {
        await supabase
          .from("feed_posts")
          .update({ body, created_at: new Date().toISOString() })
          .eq("id", existingPost.id);
      } else {
        await supabase
          .from("feed_posts")
          .insert({ user_id: userId, type: "rated_album", album_id: mine.id, body });
      }
    }

    return { ok: true, removed: false };
  });

/** Resumo de notas para vários discos (cards do Explorar). */
export const getRatingSummaries = createServerFn({ method: "GET" })
  .inputValidator((i: { discogsIds: string[] }) => ({
    discogsIds: [...new Set((i.discogsIds ?? []).filter(Boolean))].slice(0, 60),
  }))
  .handler(async ({ data }): Promise<RatingSummary[]> => {
    if (data.discogsIds.length === 0) return [];
    const sb = publicClient();
    const { data: rows } = await sb
      .from("album_track_ratings")
      .select("discogs_id, user_id, track_position, track_title, rating")
      .in("discogs_id", data.discogsIds);
    const byAlbum = new Map<string, any[]>();
    for (const r of rows ?? []) {
      const arr = byAlbum.get((r as any).discogs_id) ?? [];
      arr.push(r);
      byAlbum.set((r as any).discogs_id, arr);
    }
    return data.discogsIds.map((id) => {
      const agg = aggregate((byAlbum.get(id) ?? []) as any);
      return { discogsId: id, score: agg.score, raters: agg.raters };
    });
  });

/** Quem tem cada disco (fotinhas nos cards do Explorar). */
export const getCollectorsSummaries = createServerFn({ method: "GET" })
  .inputValidator((i: { discogsIds: string[] }) => ({
    discogsIds: [...new Set((i.discogsIds ?? []).filter(Boolean))].slice(0, 60),
  }))
  .handler(async ({ data }): Promise<CollectorsSummary[]> => {
    if (data.discogsIds.length === 0) return [];
    const sb = publicClient();
    const { data: rows } = await sb
      .from("user_albums")
      .select("discogs_id, user_id, created_at")
      .in("discogs_id", data.discogsIds)
      .eq("status", "owned")
      .order("created_at", { ascending: false });

    const byAlbum = new Map<string, string[]>();
    for (const r of rows ?? []) {
      const list = byAlbum.get(r.discogs_id) ?? [];
      if (!list.includes(r.user_id)) list.push(r.user_id);
      byAlbum.set(r.discogs_id, list);
    }
    const userIds = [...new Set([...byAlbum.values()].flatMap((l) => l.slice(0, 5)))];
    let pmap = new Map<string, CollectorAvatar>();
    if (userIds.length > 0) {
      const { data: profs } = await sb
        .from("profiles")
        .select("id, username, display_name, avatar_url")
        .in("id", userIds);
      pmap = new Map((profs ?? []).map((p: any) => [p.id, p]));
    }
    return data.discogsIds.map((id) => {
      const list = byAlbum.get(id) ?? [];
      return {
        discogsId: id,
        count: list.length,
        people: list.slice(0, 5).map((u) => pmap.get(u)).filter(Boolean) as CollectorAvatar[],
      };
    });
  });

export type UserActivityRow = {
  id: string;
  type: string;
  body: string | null;
  created_at: string;
  link?: string | null;
  context?: string | null;
  album: { id: string; title: string; artist: string; cover_url: string | null; discogs_id: string | null } | null;
};

/** Atividades públicas de um perfil (coleção, notas, comentários, feed e comunidades). */
export const listUserActivity = createServerFn({ method: "GET" })
  .inputValidator((i: { username: string }) => i)
  .handler(async ({ data }): Promise<UserActivityRow[]> => {
    const sb = publicClient();
    const { data: prof } = await sb
      .from("profiles")
      .select("id")
      .eq("username", data.username)
      .maybeSingle();
    if (!prof) return [];
    const uid = prof.id;

    const [posts, albumComments, feedPosts, feedComments, feedLikes, groupPosts, groupComments] =
      await Promise.all([
        sb
          .from("feed_posts")
          .select("id, type, body, created_at, album_id")
          .eq("user_id", uid)
          .order("created_at", { ascending: false })
          .limit(40),
        sb
          .from("album_comments")
          .select("id, body, created_at, discogs_id")
          .eq("user_id", uid)
          .order("created_at", { ascending: false })
          .limit(20),
        sb
          .from("community_posts")
          .select("id, body, created_at, kind, album")
          .eq("user_id", uid)
          .order("created_at", { ascending: false })
          .limit(20),
        sb
          .from("community_post_comments")
          .select("id, body, created_at, post_id")
          .eq("user_id", uid)
          .order("created_at", { ascending: false })
          .limit(20),
        sb
          .from("community_post_likes")
          .select("post_id, created_at")
          .eq("user_id", uid)
          .order("created_at", { ascending: false })
          .limit(20),
        sb
          .from("group_posts")
          .select("id, title, body, created_at, group_id")
          .eq("user_id", uid)
          .order("created_at", { ascending: false })
          .limit(20),
        sb
          .from("group_post_comments")
          .select("id, body, created_at, post_id")
          .eq("user_id", uid)
          .order("created_at", { ascending: false })
          .limit(20),
      ]);

    const rows: UserActivityRow[] = [];

    // Coleção / notas / anúncios (feed_posts)
    const fp = posts.data ?? [];
    const albumIds = fp.map((p: any) => p.album_id).filter(Boolean) as string[];
    let amap = new Map<string, any>();
    if (albumIds.length > 0) {
      const { data: albums } = await sb
        .from("user_albums")
        .select("id, title, artist, cover_url, discogs_id")
        .in("id", albumIds);
      amap = new Map((albums ?? []).map((a: any) => [a.id, a]));
    }
    for (const p of fp as any[]) {
      rows.push({
        id: p.id,
        type: p.type,
        body: p.body,
        created_at: p.created_at,
        link: null,
        album: p.album_id ? (amap.get(p.album_id) ?? null) : null,
      });
    }

    // Comentários em álbuns
    const ac = albumComments.data ?? [];
    if (ac.length > 0) {
      const ids = [...new Set(ac.map((c: any) => c.discogs_id))];
      const { data: embeds } = await sb
        .from("album_embeddings")
        .select("discogs_id, title, artist, cover")
        .in("discogs_id", ids);
      const emap = new Map((embeds ?? []).map((e: any) => [e.discogs_id, e]));
      for (const c of ac as any[]) {
        const e = emap.get(c.discogs_id);
        rows.push({
          id: `ac-${c.id}`,
          type: "album_comment",
          body: c.body,
          created_at: c.created_at,
          link: null,
          album: {
            id: c.discogs_id,
            title: e?.title ?? "um disco",
            artist: e?.artist ?? "",
            cover_url: e?.cover ?? null,
            discogs_id: c.discogs_id,
          },
        });
      }
    }

    // Publicações no feed
    for (const p of (feedPosts.data ?? []) as any[]) {
      const alb = p.album as any;
      rows.push({
        id: `cp-${p.id}`,
        type: "feed_post",
        body: p.body,
        created_at: p.created_at,
        link: `/explore?tab=feed&post=${p.id}`,
        album: alb?.discogs_id
          ? {
              id: alb.discogs_id,
              title: alb.title ?? "um disco",
              artist: alb.artist ?? "",
              cover_url: alb.cover ?? alb.cover_url ?? null,
              discogs_id: alb.discogs_id,
            }
          : null,
      });
    }

    // Comentários e curtidas no feed
    for (const c of (feedComments.data ?? []) as any[]) {
      rows.push({
        id: `cpc-${c.id}`,
        type: "feed_comment",
        body: c.body,
        created_at: c.created_at,
        link: `/explore?tab=feed&post=${c.post_id}`,
        album: null,
      });
    }
    for (const l of (feedLikes.data ?? []) as any[]) {
      rows.push({
        id: `cpl-${l.post_id}`,
        type: "feed_like",
        body: null,
        created_at: l.created_at,
        link: `/explore?tab=feed&post=${l.post_id}`,
        album: null,
      });
    }

    // Comunidades
    const gp = (groupPosts.data ?? []) as any[];
    const gc = (groupComments.data ?? []) as any[];
    const groupIds = [...new Set(gp.map((p) => p.group_id))];
    let gmap = new Map<string, any>();
    if (groupIds.length > 0) {
      const { data: groups } = await sb.from("groups").select("id, name, slug").in("id", groupIds);
      gmap = new Map((groups ?? []).map((g: any) => [g.id, g]));
    }
    for (const p of gp) {
      const g = gmap.get(p.group_id);
      rows.push({
        id: `gp-${p.id}`,
        type: "group_post",
        body: p.title ?? p.body,
        created_at: p.created_at,
        context: g?.name ?? null,
        link: g?.slug ? `/comunidades/${g.slug}` : null,
        album: null,
      });
    }
    if (gc.length > 0) {
      const postIds = [...new Set(gc.map((c) => c.post_id))];
      const { data: parents } = await sb
        .from("group_posts")
        .select("id, group_id")
        .in("id", postIds);
      const pmap = new Map((parents ?? []).map((p: any) => [p.id, p]));
      const extraIds = [...new Set((parents ?? []).map((p: any) => p.group_id))].filter(
        (id) => !gmap.has(id),
      );
      if (extraIds.length > 0) {
        const { data: groups } = await sb.from("groups").select("id, name, slug").in("id", extraIds);
        for (const g of groups ?? []) gmap.set((g as any).id, g);
      }
      for (const c of gc) {
        const g = gmap.get(pmap.get(c.post_id)?.group_id);
        rows.push({
          id: `gpc-${c.id}`,
          type: "group_comment",
          body: c.body,
          created_at: c.created_at,
          context: g?.name ?? null,
          link: g?.slug ? `/comunidades/${g.slug}` : null,
          album: null,
        });
      }
    }

    return rows
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
      .slice(0, 40);
  });


export type AlbumRater = {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  tracks: number;
  avg: number;
  last_at: string;
};

/** Quem avaliou faixas de um disco (público). */
export const getAlbumRaters = createServerFn({ method: "GET" })
  .inputValidator((i: { discogsId: string }) => i)
  .handler(async ({ data }): Promise<AlbumRater[]> => {
    const sb = publicClient();
    const { data: rows } = await sb
      .from("album_track_ratings")
      .select("user_id, rating, updated_at")
      .eq("discogs_id", data.discogsId);
    if (!rows || rows.length === 0) return [];

    const byUser = new Map<string, { values: number[]; last: string }>();
    for (const r of rows as any[]) {
      const cur: { values: number[]; last: string } =
        byUser.get(r.user_id) ?? { values: [] as number[], last: r.updated_at };
      cur.values.push(Number(r.rating));
      if (r.updated_at > cur.last) cur.last = r.updated_at;
      byUser.set(r.user_id, cur);
    }

    const { data: profs } = await sb
      .from("profiles")
      .select("id, username, display_name, avatar_url")
      .in("id", [...byUser.keys()]);
    const pmap = new Map((profs ?? []).map((p: any) => [p.id, p]));

    return [...byUser.entries()]
      .map(([userId, v]) => {
        const p = pmap.get(userId);
        if (!p) return null;
        return {
          id: p.id,
          username: p.username,
          display_name: p.display_name,
          avatar_url: p.avatar_url,
          tracks: v.values.length,
          avg: Math.round((v.values.reduce((a, b) => a + b, 0) / v.values.length) * 10) / 10,
          last_at: v.last,
        } as AlbumRater;
      })
      .filter(Boolean)
      .sort((a, b) => b!.tracks - a!.tracks) as AlbumRater[];
  });

/** Remove todas as minhas notas de um disco (e a atividade agrupada). */
export const clearMyAlbumRatings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: { discogsId: string }) => {
    if (!i?.discogsId) throw new Error("Disco inválido");
    return { discogsId: i.discogsId };
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { error } = await supabase
      .from("album_track_ratings")
      .delete()
      .eq("user_id", userId)
      .eq("discogs_id", data.discogsId);
    if (error) throw new Error(error.message);

    const { data: mine } = await supabase
      .from("user_albums")
      .select("id")
      .eq("user_id", userId)
      .eq("discogs_id", data.discogsId)
      .limit(1)
      .maybeSingle();

    if (mine?.id) {
      await supabase
        .from("feed_posts")
        .delete()
        .eq("user_id", userId)
        .eq("type", "rated_album")
        .eq("album_id", mine.id);
    }

    return { ok: true };
  });
